// トポロジカルソート実装

import type { DataSet, DerivedDataSet } from '../../types/dataset.types';
import logger from './logger';

/**
 * 未評価のDerivedDataSetを見つける
 * @param datasets データセットマップ（PrimaryDataSetとDerivedDataSetの両方を含む）
 * @returns 未評価のDerivedDataSetの配列
 */
export function findUnevaluatedDerivedDataSets(
  datasets: Record<string, DataSet>
): DerivedDataSet[] {
  return Object.values(datasets).filter(
    (dataset): dataset is DerivedDataSet =>
      dataset.type === 'derived' && !dataset.data
  );
}

/**
 * トポロジカルソートを実行して、依存関係順にデータセットを並べる
 *
 * 深さ優先探索（DFS）を使用して、依存関係の順序を解決します。
 * PrimaryDataSetは依存関係がないため最初に配置されます。
 * 循環依存がある場合はエラーをthrowします。
 *
 * @param datasets ソート対象のDataSet配列（PrimaryとDerivedの両方）
 * @returns 依存関係順にソートされたDataSet配列（親が先、子が後）
 * @throws {Error} 循環依存が検出された場合
 */
export function topologicalSort(datasets: DataSet[]): DataSet[] {
  const sorted: DataSet[] = [];
  const visited = new Set<string>();
  const visiting = new Set<string>();

  // データセットIDからデータセットへのマップを作成
  const datasetMap = new Map<string, DataSet>();
  for (const dataset of datasets) {
    datasetMap.set(dataset.id, dataset);
  }

  function visit(dataset: DataSet, path: string[] = []) {
    // 循環依存チェック（DerivedDataSetのみ）
    if (dataset.type === 'derived' && visiting.has(dataset.id)) {
      const cycle = [...path, dataset.id].join(' → ');
      logger.error('[TopologicalSort] Circular dependency detected', {
        cycle,
        datasetId: dataset.id,
        datasetName: dataset.name
      });
      throw new Error(`Circular dependency detected: ${cycle}`);
    }

    // 既に訪問済み
    if (visited.has(dataset.id)) {
      return;
    }

    if (dataset.type === 'derived') {
      visiting.add(dataset.id);

      // 親データセットを先に訪問
      for (const parentId of dataset.parentDatasetIds) {
        const parentDataset = datasetMap.get(parentId);
        if (parentDataset) {
          visit(parentDataset, [...path, dataset.id]);
        }
      }

      visiting.delete(dataset.id);
    }

    visited.add(dataset.id);
    sorted.push(dataset);
  }

  for (const dataset of datasets) {
    visit(dataset);
  }

  logger.debug('[TopologicalSort] Sorted datasets', {
    count: sorted.length,
    order: sorted.map(d => d.name)
  });

  return sorted;
}
